/**
 * Conversation list state for the WhatsApp Inbox (FE-05).
 *
 * Live updates arrive via WhatsAppSocketProvider (wa_message_new /
 * wa_status_update) and just trigger a re-fetch of the current page rather
 * than patching in place — the list is re-sorted by last_message_at on every
 * new message anyway, so a full re-fetch is simpler and no more expensive
 * than reconciling positions client-side. Falls back to interval polling
 * when the socket reports degraded (see useWhatsAppSocket.tsx).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  whatsappInboxService,
  WhatsAppConversation,
  ConversationStatus,
} from '@/services/integrations/whatsappInboxService';
import { useWhatsAppSocketEvents } from './useWhatsAppSocket';

const DEGRADED_POLL_MS = 10_000;
const PAGE_SIZE_DEFAULT = 20;

export interface ConversationListFilters {
  status?: ConversationStatus;
  unreadOnly?: boolean;
  assignedUserUuid?: string;
  search?: string;
}

export function useConversationList(filters: ConversationListFilters, pageSize = PAGE_SIZE_DEFAULT) {
  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const load = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const f = filtersRef.current;
      const result = await whatsappInboxService.listConversations({
        status: f.status,
        unread_only: f.unreadOnly,
        assigned_user_uuid: f.assignedUserUuid,
        search: f.search,
        page: targetPage,
        page_size: pageSize,
      });
      setConversations(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
      setPage(targetPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  // Re-fetch page 1 whenever a filter changes.
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.unreadOnly, filters.assignedUserUuid, filters.search, pageSize]);

  const refresh = useCallback(() => load(page), [load, page]);

  const { degraded, connected } = useWhatsAppSocketEvents(
    useCallback(() => {
      refresh();
    }, [refresh]),
  );

  // Socket-driven, no polling while connected: the list re-fetches on a live event,
  // and once when the socket comes back after a drop -- anything sent during the gap
  // was never pushed to us, so this is the only way to catch it.
  const wasConnected = useRef(false);
  useEffect(() => {
    if (connected && wasConnected.current === false && !loading) refresh();
    wasConnected.current = connected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // Degraded-mode fallback: the live feed can't be trusted, so poll instead.
  useEffect(() => {
    if (!degraded) return;
    const id = setInterval(refresh, DEGRADED_POLL_MS);
    return () => clearInterval(id);
  }, [degraded, refresh]);

  /** Optimistic local patch — used right after markRead/markUnread/setStatus
   * so the row updates instantly instead of waiting for the next refresh. */
  const patchConversation = useCallback((uuid: string, patch: Partial<WhatsAppConversation>) => {
    setConversations((prev) => prev.map((c) => (c.uuid === uuid ? { ...c, ...patch } : c)));
  }, []);

  return {
    conversations, total, totalPages, page, loading, error, degraded,
    goToPage: load, refresh, patchConversation,
  };
}
